import React, { useState, useEffect, use } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../components/Feedback/ConfirmDialog";
import ModalDetail from "../../components/DetailModal";
import SearchBar from "../../components/SearchBar";
import PaginatedTable from "../../components/PaginatedTable";
import ToggleSwitch from "../../components/Button/ToggleSwitch";

import { getAllAdmins } from "../../services/userServices/adminService";
import { getAllEmployees } from "../../services/userServices/employeeService";
import { getAllPartners } from "../../services/userServices/partnerService";
import {
  getAllCooperativeMembers,
  deleteCooperativeMember,
} from "../../services/CooperativeMemberServices";

const DataAnggota = () => {
  const [members, setMembers] = useState({ coop: [], nonCoop: [] });
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [toggle, setToggle] = useState("left"); // "left" = anggota, "right" = non koperasi

  const navigate = useNavigate();

  const columns =
    toggle === "left"
      ? [
          { key: "nama", label: "Nama" },
          { key: "status", label: "Kelompok" },
          { key: "memberId", label: "Member ID" },
          { key: "joinDate", label: "Join Date" },
        ]
      : [
          { key: "nama", label: "Nama" },
          { key: "status", label: "Kelompok" },
        ];

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        // fetch semua user non koperasi
        const admins = await getAllAdmins();
        const employees = await getAllEmployees();
        const partners = await getAllPartners();

        const mapData = (list, type) =>
          list
            .map((user) => ({
              id: user.id,
              nama: user.profiles?.full_name || "-",
              status:
                user.partners?.member_status?.name ||
                user.employees?.member_status?.name ||
                "-",
              memberId: null,
              joinDate: null,
              type,
            }))
            .filter((user) => {
              const normalizedStatus = (user.status || "")
                .replace(/\s/g, "")
                .toLowerCase();
              return normalizedStatus !== "nonanggota";
            });

        const nonCoop = [
          ...mapData(admins, "ADMIN"),
          ...mapData(employees, "EMPLOYEE"),
          ...mapData(partners, "PARTNER"),
        ];

        // gabung semua user biar bisa dipakai lookup user_id di cooperativeMembers
        const allUsers = [...admins, ...employees, ...partners];

        // fetch anggota koperasi
        const coopMembers = await getAllCooperativeMembers();
        const mappedCoop = coopMembers.map((m) => {
          const userDetail = allUsers.find((u) => u.id === m.user_id);
          return {
            id: m.id,
            userId: m.user_id,
            nama: userDetail?.profiles?.full_name || "-",
            status:
              userDetail?.employees?.member_status?.name ||
              userDetail?.partners?.member_status?.name ||
              "-",
            memberId: m.member_id,
            joinDate: m.join_date.split("T")[0] || "-",
            type: "COOP",
          };
        });

        setMembers({
          coop: mappedCoop,
          nonCoop: nonCoop,
        });
      } catch (error) {
        console.error("Gagal ambil data anggota:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, []);

  // data yang ditampilkan sesuai toggle
  const displayedData = (
    toggle === "left" ? members.coop : members.nonCoop
  ).filter((m) => {
    if (search && !m.nama?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  });

  const handleEdit = (index) => {
    const selected = displayedData[index];
    if (!selected) return;
    localStorage.setItem("editAnggotaKoperasi", JSON.stringify(selected));

    if (toggle === "left") {
      navigate("/anggota-koperasi/edit");
    } else {
      navigate("/anggota-koperasi/add");
    }
  };

  const handleAddNonKoperasi = (index) => {
    const selected = displayedData[index];
    if (!selected) return;
    localStorage.setItem("addAnggotaKoperasi", JSON.stringify(selected));
    navigate("/anggota-koperasi/add");
  };

  const handleView = (index) => {
    const selected = displayedData[index];
    if (!selected) return;

    const detailData = {
      Nama: selected.nama || "-",
      Kelompok: selected.status || "-",
      "Member ID": selected.memberId || "-",
      "Join Date": selected.joinDate || "-",
    };

    setSelectedMember(detailData);
    setShowDetailModal(true);
  };

  const handleDelete = async (index) => {
    const selected = displayedData[index];
    if (!selected) return;

    setSelectedMember(selected);
    setIsConfirmOpen(true); // buka dialog konfirmasi
  };
  const confirmDelete = async () => {
    if (!selectedMember) return;
    try {
      await deleteCooperativeMember(selectedMember.id); // panggil API
      setMembers((prev) => ({
        ...prev,
        coop: prev.coop.filter((m) => m.id !== selectedMember.id),
      }));
      setIsConfirmOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error("Gagal hapus anggota:", error);
    }
  };

  return (
    <div className="pt-10 pl-64">
      <h2 className="pl-6 mb-6 text-2xl font-semibold text-green-700">
        Data Anggota Koperasi
      </h2>
      <div className="p-6 mx-4 bg-white shadow rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <ToggleSwitch
            leftLabel="Anggota Koperasi"
            rightLabel="Non Koperasi"
            value={toggle}
            onChange={setToggle}
          />
          <SearchBar value={search} onChange={setSearch} />
        </div>

        {isLoading ? (
          <p className="py-8 text-center text-gray-500">
            Memuat data anggota...
          </p>
        ) : (
          <PaginatedTable
            data={displayedData}
            columns={columns}
            onEdit={toggle === "left" ? handleEdit : undefined}
            onAdd={toggle === "right" ? handleAddNonKoperasi : undefined}
            onDelete={toggle === "left" ? handleDelete : undefined}
            onView={toggle === "left" ? handleView : undefined}
            moduleName="AnggotaData"
          />
        )}

        <ConfirmDialog
          isOpen={isConfirmOpen}
          onClose={() => setIsConfirmOpen(false)}
          title="Hapus Anggota"
          message={`Apakah Anda yakin ingin menghapus ${selectedMember?.nama}?`}
          onConfirm={confirmDelete}
        />

        {showDetailModal && (
          <ModalDetail
            data={selectedMember}
            onClose={() => setShowDetailModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default DataAnggota;
